import { getPlatform, getDevicePlatform } from '../platform';

// Без фолбэка на боевой счётчик Стемса: витрина клиента, собранная
// без REACT_APP_YM_COUNTER_ID, слала бы события в чужую Метрику. Пусто/не
// число → null → события просто не отправляются (как и в index.html).
const COUNTER_ID = Number(process.env.REACT_APP_YM_COUNTER_ID) || null;

function safeYm(...args) {
  try {
    if (COUNTER_ID && typeof window !== 'undefined' && typeof window.ym === 'function') {
      window.ym(COUNTER_ID, ...args);
    }
  } catch (e) {
    // fire-and-forget: ошибки Метрики не должны ломать UI
  }
}

function baseParams() {
  return {
    platform: getPlatform(),
    device: getDevicePlatform(),
  };
}

// Просмотр страницы позиции внутри витрины (адрес сменился без перезагрузки):
// без этого Метрика видела бы весь сайт одной страницей
export function trackPageView() {
  try {
    safeYm('hit', window.location.href, { title: document.title, referer: document.referrer });
  } catch (e) {}
}

// Открыта карточка товара — ecommerce «detail» (воронка: просмотр → корзина → покупка)
export function trackProductDetail(product) {
  if (!product) return;
  pushEcommerce('detail', { products: [toProductItem(product)] });
}

export function trackGoal(name, params = {}) {
  safeYm('reachGoal', name, { ...baseParams(), ...params });
}

export function pushEcommerce(action, payload) {
  try {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ecommerce: {
        currencyCode: 'RUB',
        [action]: payload,
      },
    });
  } catch (e) {}
}

function toProductItem(product, quantity = null) {
  const item = {
    id: String(product.id),
    name: product.name || '',
    price: Number(product.price) || 0,
  };
  if (product.category?.name) item.category = product.category.name;
  if (quantity != null) item.quantity = Number(quantity) || 1;
  return item;
}

export function trackProductAddToCart(product, quantity = 1) {
  trackGoal('ecom_add_to_cart', {
    product_id: product.id,
    product_name: product.name,
    price: Number(product.price) || 0,
  });
  pushEcommerce('add', { products: [toProductItem(product, quantity)] });
}

export function trackProductRemoveFromCart(product, quantity = 1) {
  pushEcommerce('remove', { products: [toProductItem(product, quantity)] });
}

export function trackViewCart(cartItems = []) {
  trackGoal('ecom_view_cart', {
    items_count: cartItems.length,
    total: cartItems.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0),
  });
}

export function trackBeginCheckout(cartItems = [], total = 0) {
  trackGoal('ecom_begin_checkout', {
    items_count: cartItems.length,
    total: Number(total) || 0,
  });
}

export function trackAddressEntered(address) {
  trackGoal('ecom_address_entered', { address: address || '' });
}

export function trackDeliveryCalculated(result) {
  trackGoal('ecom_delivery_calculated', {
    cost: Number(result?.cost) || 0,
    zone: result?.zone || null,
    distance: result?.distance || null,
  });
}

export function trackDeliveryUnavailable(address) {
  trackGoal('ecom_delivery_unavailable', { address: address || '' });
}

export function trackDeliveryTooFar(cost, address) {
  trackGoal('ecom_delivery_too_far', {
    cost: Number(cost) || 0,
    address: address || '',
  });
}

export function trackPaymentInitiated(orderId, total) {
  trackGoal('ecom_payment_initiated', {
    order_id: orderId,
    total: Number(total) || 0,
  });
}

export function trackPurchase(order) {
  const items = (order?.items || []).map((i) => ({
    id: String(i.product_id || i.id),
    name: i.product_name || i.name || '',
    price: Number(i.price) || 0,
    quantity: Number(i.quantity) || 1,
  }));
  trackGoal('ecom_purchase', {
    order_id: order?.id,
    order_number: order?.order_number,
    total: Number(order?.total) || 0,
  });
  pushEcommerce('purchase', {
    actionField: {
      id: String(order?.order_number || order?.id || ''),
      revenue: Number(order?.total) || 0,
    },
    products: items,
  });
}

export function trackSubscriptionStarted(clientType) {
  const goal = clientType === 'individual' ? 'sub_started_individual' : 'sub_started_legal_entity';
  trackGoal(goal, { client_type: clientType });
}

export function trackSubscriptionAddressInZone(clientType) {
  const goal = clientType === 'individual' ? 'sub_address_in_zone_individual' : 'sub_address_in_zone_legal_entity';
  trackGoal(goal, { client_type: clientType });
}

export function trackSubscriptionAddressOutOfZone(clientType) {
  const goal = clientType === 'individual' ? 'sub_address_out_of_zone_individual' : 'sub_address_out_of_zone_legal_entity';
  trackGoal(goal, { client_type: clientType });
}

export function trackSubscriptionPurchased(clientType, total) {
  const goal = clientType === 'individual' ? 'sub_purchased_individual' : 'sub_purchased_legal_entity';
  trackGoal(goal, { client_type: clientType, total: Number(total) || 0 });
}

export function trackSmsRequested() {
  trackGoal('auth_sms_requested');
}

export function trackSmsVerified() {
  trackGoal('auth_sms_verified');
}

export function trackProfileOpened() {
  trackGoal('profile_opened');
}

export function trackClickTelegram(source) {
  trackGoal('click_telegram', { source: source || null });
}

export function trackClickMax(source) {
  trackGoal('click_max', { source: source || null });
}

export function trackMergeButtonClicked(target) {
  trackGoal('merge_button_clicked', { target: target || null });
}

export function trackMergeDeeplinkToTg() {
  trackGoal('merge_deeplink_to_tg');
}

export function trackMergeDeeplinkToMax() {
  trackGoal('merge_deeplink_to_max');
}

export function trackMergeSuccess(direction) {
  trackGoal('merge_success', { direction: direction || null });
}

export function trackMergeFailed(reason) {
  trackGoal('merge_failed', { reason: reason || null });
}

export function trackCertificatePurchaseStarted(amount) {
  trackGoal('certificate_purchase_started', { amount: amount || 0 });
}

export function trackCertificatePurchased(amount, orderId) {
  trackGoal('certificate_purchased', { amount: amount || 0, order_id: orderId || null });
}

export function trackCertificateActivated(amount) {
  trackGoal('certificate_activated', { amount: amount || 0 });
}
